import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
    ActionPerformed,
    PushNotificationSchema,
    PushNotifications,
    Token
} from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import * as Parse from 'parse';

/**
 * Registers the device's push token with the server via the `registerDeviceToken`
 * cloud function (End User Messaging path). Replaces the Phase 0 on-device Pinpoint /
 * Cognito registration flow.
 *
 * Anonymous: registration is keyed on the `:organizationId` URL route param, not auth.
 * `customerId` is included only when a customer context exists. Re-initializing with a
 * different org re-registers the same token under the new org (REQ-6).
 */
@Injectable({
    providedIn: 'root'
})
export class PushNotificationService {
    private listenersAttached = false;
    private currentOrganizationId: string | undefined;
    private currentCustomerId: string | undefined;
    private lastRegisteredKey: string | undefined;
    private registerInFlight: Promise<void> | undefined;

    constructor(
        private router: Router,
        private ngZone: NgZone
    ) {}

    /**
     * Request permission, attach listeners (once), and register for push.
     * Safe to call repeatedly — on org/customer change it updates the context and
     * re-registers the token under the new org.
     */
    async initialize(organizationId: string, customerId?: string): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            console.log('Push notifications are not supported on web platform');
            return;
        }

        if (!organizationId) {
            console.warn('Push registration skipped: no organizationId in route context.');
            return;
        }

        const orgChanged = organizationId !== this.currentOrganizationId;
        this.currentOrganizationId = organizationId;
        this.currentCustomerId = customerId;

        await this.createNotificationChannel();

        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
            console.error('Push notification permission not granted');
            return;
        }

        this.attachListeners();

        // register() (re-)emits the 'registration' event with the current token, so a
        // changed org re-registers the same token under the new org. orgChanged is true
        // on the first call (currentOrganizationId was undefined), covering initial setup.
        if (orgChanged) {
            await PushNotifications.register();
        }
    }

    private attachListeners(): void {
        if (this.listenersAttached) {
            return;
        }
        this.listenersAttached = true;

        // Successful registration → register the token against the current org.
        PushNotifications.addListener('registration', (token: Token) => {
            console.log('Device registered, token:', token.value);
            void this.registerToken(token.value);
        });

        PushNotifications.addListener('registrationError', error => {
            console.error('Registration error:', error);
        });

        // Incoming notification while the app is foregrounded.
        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
            console.log('Notification received:', JSON.stringify(notification));

            if (Capacitor.getPlatform() === 'android') {
                void this.showNotification(notification);
            } else {
                const deepLink = notification.data?.data?.pinpoint?.deeplink;
                this.navigateToDeepLink(deepLink);
            }
        });

        // Tap on a local (Android) notification.
        LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
            console.log('Local notification action performed:', JSON.stringify(notification));
            const deepLink = notification.notification.extra?.['pinpoint.deeplink'];
            this.navigateToDeepLink(deepLink);
        });

        // Tap on a remote push notification.
        PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
            console.log('Notification action performed:', JSON.stringify(notification));
            const platformName = Capacitor.getPlatform();
            const deepLink = platformName === 'ios'
                ? notification.notification.data.pinpoint?.deeplink
                : notification.notification.data.deepLink;
            this.navigateToDeepLink(deepLink);
        });
    }

    private async registerToken(deviceToken: string): Promise<void> {
        const organizationId = this.currentOrganizationId;
        if (!deviceToken || !organizationId) {
            return;
        }

        const key = `${deviceToken}:${organizationId}:${this.currentCustomerId ?? ''}`;
        if (this.lastRegisteredKey === key) {
            return;
        }

        if (this.registerInFlight) {
            await this.registerInFlight;
            if (this.lastRegisteredKey === key) {
                return;
            }
        }

        this.registerInFlight = this.sendTokenToServer(deviceToken, organizationId, key);
        try {
            await this.registerInFlight;
        } finally {
            this.registerInFlight = undefined;
        }
    }

    private async sendTokenToServer(
        deviceToken: string,
        organizationId: string,
        key: string
    ): Promise<void> {
        try {
            const platform = Capacitor.getPlatform();
            const params: { deviceToken: string; platform: string; organizationId: string; customerId?: string } = {
                deviceToken,
                platform,
                organizationId
            };

            if (this.currentCustomerId) {
                params.customerId = this.currentCustomerId;
            }

            const id = await Parse.Cloud.run('registerDeviceToken', params);
            this.lastRegisteredKey = key;
            console.log('Device token registered:', id);
        } catch (error) {
            console.error('Error registering device token:', error);
        }
    }

    private navigateToDeepLink(deepLink: string | undefined): void {
        if (!deepLink) {
            return;
        }

        const path = deepLink.split('://')[1];

        this.ngZone.run(() => {
            this.router.navigate(['/' + path]);
        });
    }

    private async createNotificationChannel(): Promise<void> {
        // Android-only; resolves UNIMPLEMENTED on iOS, which is benign.
        try {
            await LocalNotifications.createChannel({
                id: 'default_channel_id',
                name: 'Default Channel',
                importance: 4,
                description: 'General Notifications',
                sound: 'chaching.mp3',
                visibility: 1
            });
            console.log('Notification channel created successfully');
        } catch (err) {
            console.error('Error creating notification channel:', err);
        }
    }

    private async showNotification(notification: any): Promise<void> {
        const notificationId = Math.floor(Date.now() / 1000);

        try {
            await LocalNotifications.schedule({
                notifications: [
                    {
                        title: notification.data['pinpoint.notification.title'] || 'New Notification',
                        body: notification.data['pinpoint.notification.body'] || 'You have a new message.',
                        id: notificationId,
                        schedule: { at: new Date(Date.now() + 1000) },
                        sound: 'chaching.mp3',
                        actionTypeId: '',
                        extra: notification.data,
                        smallIcon: 'res://drawable/ic_notification_logo',
                        channelId: 'default_channel_id'
                    }
                ]
            });
            console.log('Notification scheduled successfully');
        } catch (err) {
            console.error('Error scheduling notification:', err);
        }
    }
}
